<template>
  <v-dialog v-model="showDialog" width="500px">
    <v-card class="search-modal">
      <h2 class="mb-3">Search & Add People</h2>
      <p class="mb-6">A minimum of 10 distinct members/officers/directors are needed.</p>
      <p class="mb-6">Current number of members added: <b><span :class="uniqueSelectedAffiliatePeoples.length >= 10 ? 'success-text' : 'error-text'">{{uniqueSelectedAffiliatePeoples.length}}</span></b></p>
      <v-form class="search-form" @submit.prevent="search">
        <v-text-field
          outlined
          prepend-inner-icon="mdi-magnify"
          v-model="membershipID"
          label="Search Member ID"
        ></v-text-field>
      </v-form>
      <p v-if="!searchResults.length && hasSearched">No users found</p>
      <v-data-table
        v-if="searchResults.length"
        :headers="headers"
        :items="searchResults"
        hide-default-footer
        hide-default-header
      >
        <template v-slot:item="row">
          <v-tooltip dark right>
            <template v-slot:activator="{ on, attrs }">
              <tr v-bind="attrs" v-on="on">
                <td>{{ row.item.firstName }} {{ row.item.lastName }}</td>
                <td>{{ row.item.memberId }}</td>
                <td>
                  <font-awesome-icon
                    icon="id-card"
                    :color="row.item.canBeCoowner == false ? 'red' : isValidOfficer(row.item) ? 'green' : 'gold'"
                  />
                </td>
                <td>
                  <a
                    :class="{
                      'disabled-link': isValidCoOwner(row.item) == false,
                    }"
                    v-on:click="addAffiliatePeople(row.item)"
                  >
                    <font-awesome-icon
                      icon="plus"
                      :color="isValidCoOwner(row.item) ? 'blue' : 'gray'"
                    />
                  </a>
                </td>
              </tr>
            </template>
            <span v-if="isValidCoOwner(row.item) == false"
              >This person does not meet the requirements to be an affiliate club member/officer/director.</span
            >
            <span v-else-if="isValidOfficer(row.item)">This person can be added as an affiliate member/officer/director.</span>
            <span v-else>This person can be added only as an affiliate member.</span>
          </v-tooltip>
        </template>
      </v-data-table>
      <p class="mb-6" v-if="selectedAffiliatePeoples.length">- or -</p>
      <v-btn
        color="primary"
        class="white--text"
        block
        :disabled="!selectedAffiliatePeoples.length"
        v-on:click="submit"
        >Continue</v-btn
      >
      <div v-if="selectedAffiliatePeoples.length">
        <h2 class="mt-8">Added Affiliate People</h2>
        <v-data-table
          :headers="headers"
          :items="selectedAffiliatePeoples"
          :items-per-page="selectedAffiliatePeoples.length"
          hide-default-footer
          hide-default-header
        >
          <template v-slot:item="row" >
            <tr>
              <td>{{ row.item.firstName }} {{ row.item.lastName }}</td>
              <td>{{ row.item.memberId }}</td>
              <td>
                <a v-on:click="removeAffiliatePeople(row.item)">
                  <font-awesome-icon icon="minus" />
                </a>
              </td>
            </tr>
          </template>
        </v-data-table>
      </div>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import Vue from "vue";
import { debounce } from "throttle-debounce";
import { uniqBy } from "lodash";
import registerLeaseService from "@/services/portal/registerLeaseService";
import { LesseeResult } from "@/types/portal/Lease";

export default Vue.extend({
  name: "AddPersonModal",
  data: function () {
    return {
      showDialog: this.$props.affiliatePeopleDialog,
      hasSearched: false,
      searchResults: [] as any[],
      uniqueSelectedAffiliatePeoples: [] as any[],
      selectedAffiliatePeoples: [...this.$props.affiliatePeople],
      membershipID: "",
      headers: [
        { text: "Name", align: "left", value: "name" },
        { text: "Membership ID", align: "left", value: "memberId" },
      ],
    };
  },
  watch: {
    affiliatePeopleDialog: function (newVal: boolean) {
      if (!newVal) {
        this.membershipID = "";
        this.hasSearched = false;
        this.searchResults = [] as LesseeResult[];
      }
      this.showDialog = newVal;
    },
    showDialog: function (newVal: boolean) {
      //this is handling situation when the user closes dialog
      //by clicking outside of it
      if (!newVal) {
        this.membershipID = "";
        this.hasSearched = false;
        this.searchResults = [] as LesseeResult[];
        (this.selectedAffiliatePeoples = [...this.$props.affiliatePeople]),
          this.toggleAffiliatePeopleDialog();
      }
    },
    membershipID: {
      handler: debounce(
        1000,
        function (this: any, newVal: string, oldVal: string) {
          if (newVal === oldVal || !newVal) return;
          this.search();
        }
      ),
      deep: true,
    },
    selectedAffiliatePeoples: function (){
      this.uniqueSelectedAffiliatePeoples = uniqBy(this.selectedAffiliatePeoples, mem => (mem as any).personId);
    }
  },
  methods: {
    search: function () {
      this.hasSearched = false;
      registerLeaseService
        .searchLessee(this.membershipID)
        .then((response: LesseeResult[]) => {
          this.hasSearched = true;
          this.searchResults = response;
        });
    },
    isValidCoOwner(coowner: LesseeResult) {
      return coowner.canBeCoowner;
    },
    isValidOfficer(coowner: LesseeResult) {
      return coowner.memberType === 1 || coowner.memberType === 2 || coowner.memberType === 6 || coowner.memberType === 4
    },
    addAffiliatePeople(row: LesseeResult) {
      if (!row.canBeCoowner) return;
      this.searchResults = [] as LesseeResult[];
      this.hasSearched = false;
      this.membershipID = "";
      let item = {
        firstName: row.firstName,
        lastName: row.lastName,
        memberId: row.memberId,
        personId: row.personId,
        position: row.memberType === 5 ? "Member" : "",
        memberType: row.memberType,
      };
      this.selectedAffiliatePeoples.push(item);
    },
    removeAffiliatePeople(row: LesseeResult) {
      this.selectedAffiliatePeoples.splice(this.selectedAffiliatePeoples.indexOf(row), 1);
      this.changeAffiliatePeople(this.selectedAffiliatePeoples);
    },
    submit() {
      this.changeAffiliatePeople(this.selectedAffiliatePeoples);
      this.showDialog = false;
    },
  },
  props: {
    changeAffiliatePeople: Function,
    affiliatePeopleDialog: Boolean,
    toggleAffiliatePeopleDialog: Function,
    affiliatePeople: Array,
  },
});
</script>